import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { MaskLine, FadeUp } from "../components/Reveal";
import { LogoLockup } from "../components/Logo";
import { Team } from "../components/Team";
import { VINEOPS_URL } from "../constants/site";

export default function AboutPage() {
  useEffect(() => {
    const prev = document.title;
    document.title = "About Us | Vinedresser Vineyard Management, Napa & Sonoma";
    const desc = document.querySelector('meta[name="description"]');
    const prevDesc = desc?.getAttribute("content");
    desc?.setAttribute("content", "About Vinedresser Vineyard Management, Inc. — a Napa-based vineyard management company led by owner Chris Walden, farming estates across Napa and Sonoma counties.");
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
    window.scrollTo(0, 0);
    return () => {
      document.title = prev;
      if (desc && prevDesc) desc.setAttribute("content", prevDesc);
    };
  }, []);

  return (
    <div data-testid="about-page">
      <header className="border-b border-[#E5DEC9] bg-[#F9F6F0]">
        <div className="max-w-[1440px] mx-auto flex items-center justify-between px-6 md:px-12 h-[72px]">
          <Link to="/" data-testid="about-logo-link">
            <LogoLockup size="sm" />
          </Link>
          <div className="flex items-center gap-6">
            <Link
              to="/"
              data-testid="about-back-link"
              className="group inline-flex items-center gap-2 text-[11px] tracking-[0.2em] uppercase font-semibold text-[#364132] hover:text-[#B89B67] transition-colors duration-300"
            >
              <ArrowLeft size={13} className="transition-transform duration-300 group-hover:-translate-x-0.5" />
              Back to site
            </Link>
            <a
              data-testid="about-client-login"
              href={VINEOPS_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="hidden sm:inline-flex items-center gap-2.5 text-[11px] tracking-[0.2em] uppercase font-semibold px-5 py-3 border border-[#1A3626] text-[#1A3626] transition-all duration-300 hover:bg-[#B89B67] hover:border-[#B89B67] hover:text-[#12271B]"
            >
              <img src="/assets/vineops-mark.png" alt="VineOps" className="h-[11px] w-auto" />
              Client Login
            </a>
          </div>
        </div>
      </header>

      <section className="py-24 md:py-36">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-12 md:gap-10">
          <div className="md:col-span-7">
            <FadeUp>
              <p className="text-[11px] tracking-[0.32em] uppercase text-[#B89B67] font-semibold mb-10">About Us</p>
            </FadeUp>
            <h1 className="font-display font-light text-[#1A3626] text-5xl md:text-6xl lg:text-7xl leading-[1.02] tracking-tight max-w-[14ch]">
              <MaskLine delay={0.1}>A farming</MaskLine>
              <MaskLine delay={0.22}>company, first.</MaskLine>
            </h1>
            <FadeUp delay={0.35} className="mt-10 max-w-[54ch] space-y-6">
              <p className="text-base md:text-lg leading-relaxed text-[#2C2926]/80">
                Vinedresser Vineyard Management, Inc. is a vineyard management company based in Napa, farming winegrape estates across Napa and Sonoma counties. We pair a data-driven program, every block mapped, measured, and recorded, with the judgment that only comes from decades in the field.
              </p>
              <p className="text-base md:text-lg leading-relaxed text-[#2C2926]/80">
                Vinedresser started with a love of the work itself. Chris spent years in the rows because farming was what he wanted to do, and doing it well mattered to him. Somewhere along the way he noticed something about the owners he farmed for. They cared deeply about their vineyards, but they couldn't see what was happening on them. So he built a company around both things: farming held to the highest standard, and a record of it so complete that an owner never has to wonder what happened on their ranch, or why. That record became VineOps. Twenty years later, the work is the same, and so is the reason.
              </p>
            </FadeUp>
          </div>
          <div className="md:col-span-5 md:pt-16">
            <FadeUp delay={0.25}>
              <div className="relative aspect-[3/4] overflow-hidden">
                <img
                  src="/assets/about-vineyard.jpg"
                  alt="Cabernet vineyard row with ripe fruit in Napa Valley"
                  loading="lazy"
                  decoding="async"
                  className="absolute inset-0 w-full h-full object-cover object-center"
                />
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      <Team />

      <section className="py-24 md:py-32 hairline-t">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 flex flex-col items-start gap-8">
          <FadeUp>
            <p className="text-base md:text-lg leading-relaxed text-[#2C2926]/80 max-w-[52ch]">
              Own a vineyard, planting one, or evaluating a property in Napa or Sonoma? We'd like to hear about it.
            </p>
          </FadeUp>
          <FadeUp delay={0.15}>
            <Link
              to="/#contact"
              data-testid="about-cta-button"
              className="group inline-flex items-center gap-3 bg-[#1A3626] text-[#F9F6F0] text-[11px] tracking-[0.22em] uppercase font-semibold px-10 py-4 transition-colors duration-300 hover:bg-[#B89B67] hover:text-[#12271B] active:scale-95"
            >
              Start a conversation
              <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </FadeUp>
        </div>
      </section>
    </div>
  );
}
